import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Card, CardBody, Col, Row } from "reactstrap"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import moment from "moment"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import TableContainer from "../../components/Common/TableContainer"
import { getCities as onGetCities } from "store/actions"
import { post } from "helpers/api_helper"
import { formatDate, formatINR, formatMobile } from "helpers/common_helper"
import AddSubscriptionModal from "./AddSubscriptionModal"
import SubscriptionDetailsModal from "./SubscriptionDetailsModal"

const errorMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.response?.data?.error || fallback


function Subscriptions() {
  document.title = "Subscription >> Subscriptions"

  const dispatch = useDispatch()
  const cities = useSelector(state => state.City.cities) || []
  useEffect(() => {
    dispatch(onGetCities())
  }, [dispatch])

  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState("")
  const [city, setCity] = useState("All")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const latestRequest = useRef(0)
  const [addOpen, setAddOpen] = useState(false)
  const [detailsId, setDetailsId] = useState(null)
  const [editId, setEditId] = useState(null)
  // Bumped after a manual add so the list reloads.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const requestId = ++latestRequest.current
    const load = async () => {
      setLoading(true)
      try {
        const filters = {}
        if (query.trim()) filters.search = query.trim()
        if (city && city !== "All") filters.city = city
        if (startDate) filters.startDate = startDate
        if (endDate) filters.endDate = endDate

        const data = await post("subscriptions/search", filters)
        if (requestId !== latestRequest.current) return
        setSubscriptions(Array.isArray(data) ? data : [])
      } catch (err) {
        if (requestId === latestRequest.current) {
          toast.error(errorMessage(err, "Could not load subscriptions"))
        }
      } finally {
        if (requestId === latestRequest.current) setLoading(false)
      }
    }
    load()
  }, [query, city, startDate, endDate, reloadKey])

  const columns = useMemo(
    () => [
      {
        Header: "Customer Name",
        accessor: "customerName",
        Cell: ({ row }) => {
          const c = row.original.customer
          return [c?.fname, c?.lname].filter(Boolean).join(" ") || "-"
        },
      },
      {
        Header: "Mobile",
        accessor: "mobile",
        Cell: ({ row }) => formatMobile(row.original.customer?.mobile),
      },
      {
        Header: "Package",
        accessor: "packageName",
        Cell: ({ row }) => row.original.package?.name || "-",
      },
      {
        Header: "City",
        accessor: "cityName",
        Cell: ({ row }) => row.original.city?.name || "-",
      },
      {
        Header: "Start Date",
        accessor: "startDate",
        Cell: ({ row }) => formatDate(row.original.startDate),
      },
      {
        Header: "End Date",
        accessor: "expiryDate",
        Cell: ({ row }) => formatDate(row.original.expiryDate),
      },
      {
        Header: "Turns",
        accessor: "turns",
        Cell: ({ row }) => `${row.original.usedTurns || 0} / ${row.original.turns ?? 0} used`,
      },
      {
        Header: "Amount Paid",
        accessor: "amount",
        Cell: ({ row }) => {
          const paid = Number(row.original.amount || 0) - Number(row.original.discount || 0)
          return (
            <>
              {formatINR(paid)}
              {row.original.promoCode && (
                <div className="text-muted font-size-12">{row.original.promoCode}</div>
              )}
            </>
          )
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => {
          const active = row.original.expiryDate && moment(row.original.expiryDate).isAfter(moment())
          return (
            <span
              className={`badge rounded-pill font-size-12 px-3 py-2 ${
                active ? "badge-soft-success" : "badge-soft-danger"
              }`}
            >
              {active ? "Active" : "Expired"}
            </span>
          )
        },
      },
      {
        Header: "Action",
        accessor: "action",
        disableFilters: true,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <Button
              size="sm"
              color="primary"
              outline
              onClick={() => setDetailsId(row.original._id)}
            >
              Details
            </Button>
            <Button
              size="sm"
              color="warning"
              outline
              onClick={() => setEditId(row.original._id)}
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Subscription" breadcrumbItem="Subscriptions" />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={subscriptions}
                    isCustomGlobalFilter={true}
                    searchColProps={{ md: 4, lg: 3, xl: 3 }}
                    isAddOptions={true}
                    title="Subscription"
                    handleOrderClicks={() => setAddOpen(true)}
                    alignAddWithInputs={true}
                    setQuery={setQuery}
                    query={query}
                    totals={subscriptions.length}
                    isStartDate={true}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    isEndDate={true}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    DateFilterTitle={"Start Date"}
                    isCityFilter={true}
                    cities={cities}
                    city={city}
                    setCity={setCity}
                    customPageSize={10}
                    isPagination={true}
                    filterable={false}
                    tableClass="align-middle table-nowrap table-check"
                    theadClass="table-light"
                    pagination="pagination pagination-rounded justify-content-end mb-2"
                  />
                  {!loading && subscriptions.length === 0 && (
                    <p className="text-center text-muted mb-0 mt-3">No subscriptions found</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      {/* Same modal for Add and Edit (edit when subscriptionId is set) */}
      <AddSubscriptionModal
        isOpen={addOpen || !!editId}
        subscriptionId={editId}
        toggle={() => {
          setAddOpen(false)
          setEditId(null)
        }}
        onSaved={() => setReloadKey(k => k + 1)}
      />
      <SubscriptionDetailsModal subscriptionId={detailsId} toggle={() => setDetailsId(null)} />
      <ToastContainer />
    </React.Fragment>
  )
}

export default Subscriptions
